import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { SiteObject } from './SiteObject';
import {
  applySiteObjectStyle,
  fitScale,
  seatingOffset,
  type SiteModelFit,
  type SiteModelStyle,
  type SiteObjectModelProvider,
  type SiteObjectModelRequest,
} from './SiteObjectModelSource';

/**
 * glTF-backed site objects — benches, fire pits, lamp posts, boulders, planters.
 *
 * Kept in its own module so the loader is only bundled by callers that actually use it;
 * `SiteObjectWorld` depends on the registry, never on this file. That is the same arrangement
 * `FloraFactory` has with `GltfFloraModel`, and it is what lets a build that ships no assets avoid
 * paying for a glTF parser.
 *
 * Usage:
 * ```ts
 * const prototype = await loadSiteModel('/assets/props/bench-slatted.glb');
 * siteModels.register('bench-slatted', new GltfSiteObjectProvider('bench-slatted', prototype, {
 *   fit: 'uniform',
 *   style: 'house',
 * }));
 * ```
 *
 * In practice `PropLibrary` does this from the catalog rather than anyone calling it by hand.
 */

export interface GltfSiteObjectProviderOptions {
  /** How the asset is scaled to the record's dimensions. Defaults to `uniform`. */
  fit?: SiteModelFit;
  /** How much of the asset's own material treatment survives. Defaults to `house`. */
  style?: SiteModelStyle;
  /**
   * Extra rotation about +Y applied before the record's own, in radians.
   *
   * Assets do not agree on which way is forward. Rather than re-export a downloaded mesh to fix its
   * facing, the catalog records the correction once and every instance inherits it — so a user who
   * rotates a bench to 0° gets a bench facing the way the catalog says it faces.
   */
  yawOffset?: number;
  /**
   * How far to sink the asset into the ground, in meters.
   *
   * A small bite stops a prop from appearing to hover on ground that is never perfectly flat under
   * it. Floating reads as a bug; slightly buried reads as settled.
   */
  sinkMeters?: number;
}

/** Load a glTF/GLB file and return its scene root, ready to hand to {@link GltfSiteObjectProvider}. */
export async function loadSiteModel(url: string, loader = new GLTFLoader()): Promise<THREE.Object3D> {
  const gltf = await loader.loadAsync(url);
  return gltf.scene;
}

export class GltfSiteObjectProvider implements SiteObjectModelProvider {
  /**
   * Measured once, on the prototype, and reused by every instance.
   *
   * Both halves matter: `Box3.setFromObject` walks the whole subtree and is far too expensive to
   * repeat per placement, and measuring once guarantees that twelve copies of the same bench are
   * seated and scaled identically rather than each re-deriving a slightly different answer.
   */
  private readonly seating: { offset: THREE.Vector3; size: THREE.Vector3 };

  constructor(
    readonly id: string,
    private readonly prototype: THREE.Object3D,
    private readonly options: GltfSiteObjectProviderOptions = {},
  ) {
    this.seating = seatingOffset(prototype);
  }

  /** The asset's own size in meters, before any fitting. The catalog reports this to the inspector. */
  get naturalSize(): THREE.Vector3 {
    return this.seating.size.clone();
  }

  build(request: SiteObjectModelRequest): THREE.Object3D | null {
    const object = request.object;

    // `clone(true)` shares geometry with the prototype — which is the point, since a courtyard of
    // twelve chairs should hold one set of buffers. Materials are replaced per instance below, so
    // nothing mutable is shared. The view therefore owns its materials and *not* its geometry, which
    // is why `SiteObjectWorld` disposes model-backed views differently.
    const inner = this.prototype.clone(true);
    inner.position.copy(this.seating.offset);

    const scale = fitScale(this.seating.size, dimensionsOf(object), this.options.fit ?? 'uniform');

    // A wrapper carries the scale and yaw correction so the measured seating offset above stays in
    // the asset's own units — applying both to one node would scale the offset twice.
    const root = new THREE.Group();
    root.add(inner);
    root.scale.copy(scale);
    root.rotation.y = this.options.yawOffset ?? 0;
    root.position.y = -(this.options.sinkMeters ?? 0.02);

    applySiteObjectStyle(root, object, this.options.style ?? 'house');
    return root;
  }
}

/**
 * The box a record asks its asset to fit into.
 *
 * Height falls back to the footprint's smaller side when the record carries none, so a prop created
 * before anyone set a height still scales sensibly instead of collapsing. Paving and buildings pass
 * their real height; props get one from the catalog at creation.
 */
function dimensionsOf(object: SiteObject): { width: number; depth: number; height: number } {
  const width = object.widthMeters;
  const depth = object.depthMeters;
  const height = object.heightMeters > 0 ? object.heightMeters : Math.min(width, depth);
  return { width, depth, height };
}
